import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ShoppingCart, User, Menu, LogIn, UserPlus, X, ChevronRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/contexts/CartContext";
import { CartDrawer } from "../cart/CartDrawer";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

// Add this component for mobile menu items
const MobileNavItem = ({ href, label, onClick }: { href: string; label: string; onClick?: () => void }) => {
  const location = useLocation();
  const isActive = location.pathname === href;

  return (
    <Link
      to={href}
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all",
        isActive
          ? "bg-accent text-accent-foreground"
          : "hover:bg-accent/50"
      )}
    >
      <span>{label}</span>
      <ChevronRight className="h-4 w-4 ml-auto" />
    </Link>
  );
};

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hideNavbar, setHideNavbar] = useState(false);
  const lastScrollY = useRef(0);
  const isTicking = useRef(false);
  const scrollTimeout = useRef<NodeJS.Timeout>();
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const { user, firebaseUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { cartItems } = useCart();
  
  // Check if we should hide the navbar (for partner routes)
  const shouldHideNavbar = location.pathname.startsWith('/partner');

  const cartItemCount = useMemo(() => 
    cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems]
  );
  const isAdmin = user?.role === "admin";

  const navItems = useMemo(() => [
    { href: "/", label: "Home" },
    { href: "/products", label: "Shop" },
    { href: "/fragrances", label: "Fragrances" },
    { href: "/tales", label: "Tales" },
    ...(isAdmin ? [{ href: "/admin", label: "Admin" }] : []),
    // Partner users with partnerCode can access partner dashboard
    ...(user?.partnerCode ? [{ href: "/partner/fragrance", label: "Partner Dashboard" }] : []),
  ], [isAdmin, user?.partnerCode]);

  // Optimized scroll behavior with debounce
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (!isTicking.current) {
        window.requestAnimationFrame(() => {
          // Clear existing timeout
          if (scrollTimeout.current) {
            clearTimeout(scrollTimeout.current);
          }

          // Apply shadow immediately when scrolled
          setScrolled(currentScrollY > 10);

          // Debounce navbar hide/show for smoother interaction
          scrollTimeout.current = setTimeout(() => {
            if (currentScrollY > 100) {
              setHideNavbar(currentScrollY > lastScrollY.current);
            } else {
              setHideNavbar(false);
            }
          }, 50);
          
          lastScrollY.current = currentScrollY;
          isTicking.current = false;
        });

        isTicking.current = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, []); // Empty dependency array ensures single event binding

  // Handle click outside of mobile menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobileMenuOpen && 
          mobileMenuRef.current && 
          menuButtonRef.current && 
          !mobileMenuRef.current.contains(event.target as Node) && 
          !menuButtonRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    // Handle escape key press
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isMobileMenuOpen]);

  const handleSignOut = useCallback(async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  }, [logout, navigate]);

  const renderUserMenu = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-full hover:bg-background/80 transition-all">
          <Avatar className="h-8 w-8 border border-primary/10 transition-all hover:border-primary/30">
            <AvatarImage src={undefined} />
            <AvatarFallback className="bg-primary/5">
              {firebaseUser?.displayName?.[0]?.toUpperCase() || <User className="h-4 w-4 [filter:brightness(0)_saturate(100%)]" />}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 p-2 backdrop-blur-md bg-background/95 border border-border/50 shadow-lg rounded-xl">
        <DropdownMenuLabel className="font-normal px-3 py-2">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-semibold leading-none">{firebaseUser?.displayName || "User"}</p>
            {user && (
              <p className="text-xs text-muted-foreground truncate">
                {user.email}
              </p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="my-1 bg-primary/10" />
        <div className="px-1">
          <DropdownMenuItem asChild className="rounded-lg py-2 px-3 cursor-pointer flex items-center gap-2 text-sm">
            <Link to="/profile" className="w-full">
              <User className="h-4 w-4 opacity-70 [filter:brightness(0)_saturate(100%)]" />
              <span>Profile</span>
            </Link>
          </DropdownMenuItem>
        </div>
        <DropdownMenuSeparator className="my-1 bg-primary/10" />
        <div className="px-1">
          <DropdownMenuItem
            className="rounded-lg py-2 px-3 cursor-pointer text-red-600 flex items-center gap-2 text-sm"
            onClick={handleSignOut}
          >
            <svg className="h-4 w-4 opacity-70 [filter:brightness(0)_saturate(100%)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span>Sign out</span>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const renderAuthButtons = () => (
    <div className="hidden sm:flex items-center gap-1.5">
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => navigate("/login")}
        className="h-8 text-black hover:text-black transition-all hover:bg-background/80 px-2.5 sm:px-3"
      >
        <LogIn className="h-3.5 w-3.5 sm:mr-1.5 [filter:brightness(0)_saturate(100%)]" />
        <span className="hidden sm:inline text-xs font-bold">Sign in</span>
      </Button>
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => navigate("/signup")}
        className="h-8 text-black border-primary/20 hover:bg-primary/5 hover:border-primary/40 transition-all px-2.5 sm:px-3"
      >
        <UserPlus className="h-3.5 w-3.5 sm:mr-1.5 [filter:brightness(0)_saturate(100%)]" />
        <span className="hidden sm:inline text-xs font-bold">Sign up</span>
      </Button>
    </div>
  );

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      setIsMobileMenuOpen(false);
      setIsCartOpen(false);
    };
  }, []);

  // Close sheets when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsCartOpen(false);
  }, [location.pathname]);

  // Optimized mobile navigation handlers with useCallback
  const [isMenuClickable, setIsMenuClickable] = useState(true);
  const clickTimeout = useRef<NodeJS.Timeout>();
  
  const handleMobileNavClick = useCallback(() => {
    if (clickTimeout.current) {
      clearTimeout(clickTimeout.current);
    }
    setIsMobileMenuOpen(false);
  }, []);
  
  // Enhanced menu toggle with better touch response
  const handleMenuToggle = useCallback(() => {
    if (isMenuClickable) {
      setIsMenuClickable(false);
      setIsMobileMenuOpen(prev => !prev);
      
      // Clear existing timeout
      if (clickTimeout.current) {
        clearTimeout(clickTimeout.current);
      }
      
      // Re-enable clicking with optimized delay
      clickTimeout.current = setTimeout(() => setIsMenuClickable(true), 150);
    }
  }, [isMenuClickable]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (clickTimeout.current) {
        clearTimeout(clickTimeout.current);
      }
    };
  }, []);

  // If we're on a partner route, return an empty fragment instead of the navbar
  if (shouldHideNavbar) {
    return <></>;
  }
  
  return (
    <motion.header 
      className={cn(
        "sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/60 transition-all duration-300",
        scrolled ? "shadow-sm border-transparent" : "border-border/20",
        hideNavbar && "-translate-y-full"
      )}
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: hideNavbar ? -100 : 0, opacity: 1 }}
      transition={{ 
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1], // Custom cubic bezier for smooth easing
        opacity: { duration: 0.3 },
        y: { type: "spring", stiffness: 100, damping: 20 }
      }}
    >
      <div className="container px-4 mx-auto">
        <div className="flex h-14 items-center justify-between">
          {/* Left section: Mobile hamburger menu + desktop nav */}
          <div className="flex-1 flex items-center justify-start space-x-1 sm:space-x-2">
            <motion.button
              ref={menuButtonRef}
              initial={false}
              animate={isMobileMenuOpen ? { rotate: 90, scale: 1.1 } : { rotate: 0, scale: 1 }}
              transition={{ 
                duration: 0.2,
                ease: [0.22, 1, 0.36, 1],
                scale: { duration: 0.1 },
                rotate: { type: "spring", stiffness: 300, damping: 20 }
              }}
              whileTap={{ scale: 0.92 }}
              className="relative flex md:hidden items-center justify-center h-8 w-8 rounded-full hover:bg-accent/50 transition-all"
              onClick={handleMenuToggle}
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              <AnimatePresence mode="wait" initial={false}>
                {isMobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ opacity: 0, rotate: -45, scale: 0.5 }}
                    animate={{ opacity: 1, rotate: 0, scale: 1 }}
                    exit={{ opacity: 0, rotate: 45, scale: 0.5 }}
                    transition={{ duration: 0.1, ease: [0.23, 1, 0.32, 1] }}
                  >
                    <X className="h-4 w-4" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ opacity: 0, rotate: 45, scale: 0.5 }}
                    animate={{ opacity: 1, rotate: 0, scale: 1 }}
                    exit={{ opacity: 0, rotate: -45, scale: 0.5 }}
                    transition={{ duration: 0.1, ease: [0.23, 1, 0.32, 1] }}
                  >
                    <Menu className="h-4 w-4" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
            
            {/* Desktop navigation links - left side */}
            <div className="hidden md:flex items-center space-x-0.5">
              {navItems.map((item) => (
                <Link 
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "px-2.5 py-1 text-xs rounded-sm transition-all duration-200 font-bold",
                    location.pathname === item.href
                      ? "text-black"
                      : "text-black hover:text-black hover:bg-accent/50"
                  )}
                  style={{ fontFamily: 'Arial, sans-serif', letterSpacing: '0.03em' }}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
          
          {/* Center section - Brand logo */}
          <div className="flex-1 flex justify-center items-center">
            <Link 
              to="/" 
              className="flex flex-col items-center transition-colors relative group"
            >
              <img 
                src="/logo.svg" 
                alt="TEL U ASEGH" 
                className="h-8 w-auto transition-transform duration-300 group-hover:scale-105"
              />
            </Link>
          </div>
          
          {/* Right section - Cart + User */}
          <div className="flex-1 flex items-center justify-end space-x-1 sm:space-x-2">
            
            <Button
              variant="ghost"
              size="icon"
              className="relative h-8 w-8 rounded-full hover:bg-accent/50 transition-all"
              onClick={() => setIsCartOpen(true)}
            >
              <img 
                src="/tel_add_to_cart.svg" 
                alt="Cart" 
                className="h-5 w-5 transition-transform duration-300 group-hover:scale-105 [filter:brightness(0)_saturate(100%)]"
              />
              {cartItemCount > 0 && (
                <Badge
                  variant="secondary"
                  className="absolute -right-1 -top-1 h-3.5 min-w-[14px] px-1 text-[8px] bg-primary text-primary-foreground"
                >
                  {cartItemCount}
                </Badge>
              )}
              <span className="sr-only">Open cart</span>
            </Button>

            {user ? renderUserMenu() : (
              <>
                {renderAuthButtons()}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            ref={mobileMenuRef}
            initial={{ opacity: 0, y: -10, scale: 0.98 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              transition: {
                duration: 0.25,
                ease: [0.22, 1, 0.36, 1],
                staggerChildren: 0.05,
                delayChildren: 0.05
              }
            }}
            exit={{ 
              opacity: 0, 
              y: -15, 
              scale: 0.98,
              transition: {
                duration: 0.2,
                ease: [0.22, 1, 0.36, 1]
              }
            }}
            className="md:hidden fixed inset-x-0 top-[3.5rem] p-4 bg-background/95 backdrop-blur-lg border-b space-y-2 shadow-lg max-h-[calc(100vh-3.5rem)] overflow-y-auto"
          >
            <motion.div
              variants={{
                hidden: { opacity: 0, y: -20 },
                visible: { opacity: 1, y: 0 }
              }}
              initial="hidden"
              animate="visible"
              className="space-y-2"
            >
              {navItems.map((item, index) => (
                <motion.div
                  key={item.href}
                  variants={{
                    hidden: { opacity: 0, x: -20, scale: 0.95 },
                    visible: { 
                      opacity: 1, 
                      x: 0,
                      scale: 1,
                      transition: {
                        delay: index * 0.07,
                        duration: 0.35,
                        ease: [0.22, 1, 0.36, 1],
                        scale: {
                          type: "spring",
                          stiffness: 100,
                          damping: 15
                        }
                      }
                    }
                  }}
                >
                  <MobileNavItem
                    href={item.href}
                    label={item.label}
                  />
                </motion.div>
              ))}
            </motion.div>
            
            {!user && (
              <motion.div 
                className="pt-2 mt-2 border-t border-border/50"
                variants={{
                  hidden: { opacity: 0, y: -10, scale: 0.95 },
                  visible: { 
                    opacity: 1, 
                    y: 0,
                    scale: 1,
                    transition: {
                      delay: 0.25,
                      duration: 0.35,
                      ease: [0.22, 1, 0.36, 1],
                      scale: {
                        type: "spring",
                        stiffness: 100,
                        damping: 15
                      }
                    }
                  }
                }}
                initial="hidden"
                animate="visible"
              >
                <MobileNavItem
                  href="/login"
                  label="Sign in"
                  onClick={handleMobileNavClick}
                />
                <MobileNavItem
                  href="/signup"
                  label="Sign up"
                  onClick={handleMobileNavClick}
                />
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cart Drawer */}
      <CartDrawer 
        isOpen={isCartOpen} 
        onClose={() => setIsCartOpen(false)} 
      />
    </motion.header>
  );
};

export default Navbar;
